/**
 * 統一エラーハンドリングユーティリティ
 * FastAPI形式のエラーレスポンスを一元的に処理する
 */

import { translateErrorMessage } from './errorMessages';
import { logger, errorReporter } from './logger';

interface ApiError {
  detail?: string | Array<{ msg: string; loc?: string | string[]; type?: string }>;
  error?: string;
  message?: string;
  [key: string]: any;
}

interface HttpError extends Error {
  status?: number;
  data?: any;
}

/**
 * APIエラーレスポンスを解析してユーザーフレンドリーなメッセージを返す
 * @param error - エラーオブジェクト
 * @param defaultMessage - デフォルトエラーメッセージ
 * @returns ユーザーに表示するエラーメッセージ
 */
export const handleApiError = (
  error: Error | ApiError | unknown,
  defaultMessage: string = 'エラーが発生しました'
): string => {
  // ネットワークエラーやAPIサーバーダウンの場合
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return 'サーバーに接続できません。ネットワーク接続を確認してください。';
  }

  // Error オブジェクトの場合はmessageを取得
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // API レスポンスエラーの場合
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiError;

    // FastAPI形式: detail フィールド（配列または文字列）
    if (apiError.detail) {
      // 配列の場合（バリデーションエラー）
      if (Array.isArray(apiError.detail)) {
        return apiError.detail
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            // Pydantic バリデーションエラー形式
            if (item.msg && item.loc) {
              const field = Array.isArray(item.loc) ? item.loc.join('.') : item.loc;
              return `${field}: ${item.msg}`;
            }
            return item.msg || JSON.stringify(item);
          })
          .join(', ');
      }
      // 文字列の場合
      return apiError.detail;
    }

    // Mock形式: error フィールド（後方互換性）
    if (apiError.error) {
      return apiError.error;
    }

    // その他のエラー形式
    if (apiError.message) {
      return apiError.message;
    }
  }

  return defaultMessage;
};

/**
 * APIリクエスト用のエラーハンドリング関数
 * fetchのレスポンスを処理してエラーをthrowする
 * @param response - fetch APIのレスポンス
 * @param defaultMessage - デフォルトエラーメッセージ
 * @throws パースされたエラーメッセージ
 */
export const handleApiResponse = async (
  response: Response,
  defaultMessage: string = 'APIエラーが発生しました'
): Promise<Response> => {
  if (!response.ok) {
    let errorData: any;
    let errorMessage: string;

    try {
      errorData = await response.json();
      errorMessage = handleApiError(errorData, defaultMessage);
    } catch (parseError) {
      // JSON パースに失敗した場合はHTTPステータスベースのメッセージ
      errorMessage = getHttpStatusMessage(response.status) || defaultMessage;
    }

    const error = new Error(errorMessage) as HttpError;
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  return response;
};

/**
 * HTTPステータスコードに基づくエラーメッセージを返す
 * @param status - HTTPステータスコード
 * @returns エラーメッセージ
 */
const getHttpStatusMessage = (status: number): string => {
  switch (status) {
    case 400:
      return 'リクエストに問題があります';
    case 401:
      return '認証が必要です。再度ログインしてください';
    case 403:
      return 'アクセス権限がありません';
    case 404:
      return '要求されたデータが見つかりません';
    case 409:
      return 'データの競合が発生しました';
    case 422:
      return '入力データに問題があります';
    case 500:
      return 'サーバー内部エラーが発生しました';
    case 502:
      return 'サーバーが一時的に利用できません';
    case 503:
      return 'サービスが一時的に利用できません';
    default:
      return `エラーが発生しました (${status})`;
  }
};

interface ErrorDetails {
  timestamp: string;
  context: string;
  errorType: string;
  message: string;
  stack?: string;
  originalError: any;
}

/**
 * ログ出力用のエラー詳細情報を生成
 * @param error - エラーオブジェクト
 * @param context - エラーが発生したコンテキスト
 * @returns ログ用の詳細情報
 */
export const getErrorDetails = (error: any, context: string = ''): ErrorDetails => {
  return {
    timestamp: new Date().toISOString(),
    context,
    errorType: error?.constructor?.name || typeof error,
    message: error?.message || error,
    stack: error?.stack,
    originalError: error
  };
};

/**
 * 統合エラーログ出力（構造化ログ対応）
 * @param error - エラーオブジェクト
 * @param context - エラーが発生したコンテキスト
 * @param additionalData - 追加のデータ
 */
export const logError = (
  error: any,
  context: string = '',
  additionalData: Record<string, any> = {}
): void => {
  const errorDetails = getErrorDetails(error, context);

  // 構造化ログで記録
  logger.error(errorDetails.message, {
    context,
    errorType: errorDetails.errorType,
    stack: errorDetails.stack,
    ...additionalData
  });

  // エラーレポート生成と保存
  if (error instanceof Error) {
    const report = errorReporter.generateReport(error, {
      context,
      ...additionalData
    });
    errorReporter.saveToLocalStorage(report);
  }
};

/**
 * エラーメッセージに対応するガイダンス情報を取得
 * @param errorMessage - エラーメッセージ
 * @param statusCode - HTTPステータスコード
 * @returns ガイダンステキスト
 */
export const getErrorGuidance = (errorMessage: string, statusCode?: number): string | null => {
  // ネットワークエラー
  if (errorMessage.includes('サーバーに接続できません')) {
    return 'インターネット接続を確認し、しばらく待ってから再度お試しください。問題が続く場合は管理者にお問い合わせください。';
  }

  // 認証エラー
  if (statusCode === 401 || errorMessage.includes('認証')) {
    return 'ログイン画面から再度ログインしてください。スタッフIDやPINが正しいことを確認してください。';
  }

  // バリデーションエラー
  if (statusCode === 422 || errorMessage.includes('入力データに問題')) {
    return 'フォームの入力内容を確認してください。必須項目が未入力でないか、形式が正しいかをチェックしてください。';
  }

  // データ重複エラー
  if (statusCode === 409 || errorMessage.includes('既に存在')) {
    return '同じ情報が既に登録されています。既存のデータを確認するか、異なる値で再度お試しください。';
  }

  // 入金関連エラー
  if (errorMessage.includes('入金金額')) {
    return '入金金額は1円以上100万円以下の範囲で入力してください。数値のみを入力し、カンマや円マークは含めないでください。';
  }

  // 残高不足エラー
  if (errorMessage.includes('残高') || errorMessage.includes('不足')) {
    return '利用金額が現在の残高を超えています。残高を確認し、入金を行うか利用金額を調整してください。';
  }

  // 顧客関連エラー
  if (errorMessage.includes('顧客') || errorMessage.includes('電話番号')) {
    return '顧客名は2文字以上、電話番号は10-11桁で入力してください。電話番号はハイフンなしで入力してください。';
  }

  // サーバーエラー
  if (statusCode && statusCode >= 500) {
    return 'システムで一時的な問題が発生しています。しばらく待ってから再度お試しください。問題が続く場合は管理者にお問い合わせください。';
  }

  return null;
};

export type ErrorSeverity = 'info' | 'warning' | 'error';

/**
 * エラーの重要度を判定する
 * @param errorMessage - エラーメッセージ
 * @param statusCode - HTTPステータスコード
 * @returns 重要度 ('info', 'warning', 'error')
 */
export const getErrorSeverity = (errorMessage: string, statusCode?: number): ErrorSeverity => {
  // 重大なエラー（認証、サーバーエラー）
  if (statusCode === 401 || (statusCode && statusCode >= 500)) {
    return 'error';
  }

  // 警告レベル（データ競合、権限など）
  if (statusCode === 403 || statusCode === 409) {
    return 'warning';
  }

  // 情報レベル（バリデーションエラーなど）
  if (statusCode === 400 || statusCode === 422) {
    return 'info';
  }

  return 'error';
};

/**
 * API リクエスト開始時のログ記録
 * @param method - HTTPメソッド
 * @param url - リクエストURL
 * @param params - リクエストパラメータ
 */
export const logApiRequest = (
  method: string,
  url: string,
  params: Record<string, any> = {}
): void => {
  logger.apiRequest(method, url, { params });
};

/**
 * API レスポンス受信時のログ記録
 * @param method - HTTPメソッド
 * @param url - リクエストURL
 * @param status - ステータスコード
 * @param duration - レスポンス時間
 * @param responseData - レスポンスデータ（エラー時のみ）
 */
export const logApiResponse = (
  method: string,
  url: string,
  status: number,
  duration: number,
  responseData: any = null
): void => {
  logger.apiResponse(
    method,
    url,
    status,
    duration,
    status >= 400 && responseData ? { errorData: responseData } : {}
  );
};

/**
 * ユーザーアクション記録
 * @param action - アクション名
 * @param details - アクション詳細
 */
export const logUserAction = (action: string, details: Record<string, any> = {}): void => {
  logger.userAction(action, details);
};

/**
 * パフォーマンス計測用のログ
 * @param operation - 操作名
 * @param duration - 処理時間（ミリ秒）
 * @param metadata - 追加のメタデータ
 */
export const logPerformance = (
  operation: string,
  duration: number,
  metadata: Record<string, any> = {}
): void => {
  const level = duration > 3000 ? 'warn' : duration > 1000 ? 'info' : 'debug';
  logger[level](`Performance: ${operation} completed in ${duration}ms`, {
    context: 'performance',
    operation,
    duration,
    ...metadata
  });
};

export { translateErrorMessage };
